import React, { useEffect, useState } from "react";
import { Select, Button, Space, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import { IStore } from "../../../models/store/response";
import { createActiveNamlieahStoreFilters } from "../../../utils/helpers/store-filters";

const { Option } = Select;

interface Props {
  value?: number;
  onChange: (storeId: number) => void;
  disabled?: boolean;
  placeholder?: string;
}

const StoreSelector: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const [stores, setStores] = useState<IStore[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const response = await EndPoints.store.getAllStores({
        perPage: -1, // Get all stores
        light: true, // Light response
        filters: createActiveNamlieahStoreFilters(),
      });

      if (response.data?.data) {
        setStores(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  return (
    <Space.Compact style={{ width: "100%" }}>
      <Select
        value={value}
        onChange={onChange}
        disabled={disabled || loading}
        placeholder={placeholder || t("select_store")}
        showSearch
        filterOption={(input, option) =>
          (option?.children as unknown as string)
            ?.toLowerCase()
            .includes(input.toLowerCase())
        }
        style={{ width: "100%" }}
        notFoundContent={
          loading ? <Spin size="small" /> : t("no_stores_available")
        }
        aria-label={placeholder || t("select_store")}
      >
        {stores.map((store) => (
          <Option key={store.id} value={store.id}>
            {store.name}
          </Option>
        ))}
      </Select>
      <Button
        icon={<ReloadOutlined />}
        onClick={fetchStores}
        disabled={disabled || loading}
        loading={loading}
        title={t("refresh_stores")}
        aria-label={t("refresh_stores")}
      />
    </Space.Compact>
  );
};

export default StoreSelector;
